import * as React from 'react';
import { AddressElement, CardElement } from '@stripe/react-stripe-js';
import {
    CreateTokenCardData,
    StripeAddressElementOptions,
    StripeCardElement,
    StripeCardElementOptions,
    TokenResult
} from '@stripe/stripe-js';
import { BillingAddress } from 'fs-api-client';
import { StripeBillingInputFormProperties } from '../interfaces/BillingDetailsInterfaces';
import { TaxInfo, TaxStatus } from '../interfaces/TaxInfo';
import { StripeTaxStatusDropdown } from './StripeTaxStatusDropdown';
import { StripeTaxIdInput } from './StripeTaxIdInput';

interface StripeAddressValue {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postal_code: string;
    country: string;
}

interface StripeAddressElementValue {
    address: StripeAddressValue;
    name: string;
}

interface BillingDetailsFormState {
    saving: boolean;
    isCardDataComplete: boolean;
    isInputValid: boolean;
    selectedCountry: string;
    taxId?: string;
    taxStatus?: TaxStatus;
}

const STRIPE_US_COUNTRY_CODE = 'US';

export class StripeBillingDetailsInputForm extends React.Component<StripeBillingInputFormProperties, BillingDetailsFormState> {
    constructor(props: any) {
        super(props);

        this.handleChangeClick = this.handleChangeClick.bind(this);
        this.handleCardDataChange = this.handleCardDataChange.bind(this);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleAddressDataChange = this.handleAddressDataChange.bind(this);
        this.handleTaxStatusChange = this.handleTaxStatusChange.bind(this);
        this.handleTaxCodeChange = this.handleTaxCodeChange.bind(this);

        this.state = {
            saving: false,
            isCardDataComplete: false,
            isInputValid: false,
            selectedCountry: '',
            taxId: undefined,
            taxStatus: undefined,
        };
    }

    componentDidMount() {

        if (this.props.taxInfo?.taxStatus) {
            this.setState({ taxStatus: this.props.taxInfo.taxStatus });
        }

        if (this.props.taxInfo?.taxId) {
            this.setState({ taxId: this.props.taxInfo.taxId });
        }

        if (this.props.billingAddress?.country) {
            this.setState({selectedCountry: this.props.billingAddress.country});
        }

        if (this.props.isCommitInitiated) {
            this.commit();
        }
    }

    componentDidUpdate() {
        if (this.props.isCommitInitiated) {
            this.commit();
        }
    }

    render() {

        if (this.props.cardData) {
            return (
                <div className="static__card stripe-custom-field">
                    <label>Billing details</label>
                    <div className="row">
                        <span className="col-7">**** **** **** {this.props.cardData!.numberLast4}</span>
                        <span className="col-3 right">{this.props.cardData!.expiryMonth}/{this.props.cardData!.expiryYear}</span>
                        <span className="col-2 right minitext">
                            <a href="#" onClick={this.handleChangeClick}>Change</a>
                        </span>
                        {this.getBillingAddressSection()}
                    </div>

                </div>
            );
        } else {
            const cardElementOptions = this.getCardElementOptions();
            const addressElementOptions = this.getAddressElementOptions();
            return (
                <form>
                    <div className="stripe-custom-field">
                        <label>Credit card</label>
                        <CardElement
                            options={cardElementOptions}
                            className="stripe-card"
                            onChange={this.handleCardDataChange}
                        />
                    </div>
                    <div>
                        <label>Billing address</label>
                        <AddressElement
                            options={addressElementOptions}
                            className="StripeElement"
                            onChange={this.handleAddressDataChange}
                        />
                    </div>
                    {this.state.selectedCountry !== STRIPE_US_COUNTRY_CODE &&
                        <div>
                            <StripeTaxIdInput
                                value={this.props.taxInfo?.taxId}
                                onChange={this.handleTaxCodeChange}
                            />
                        </div>
                    }
                    {this.state.selectedCountry === STRIPE_US_COUNTRY_CODE &&
                        <div>
                            <StripeTaxStatusDropdown
                                value={this.props.taxInfo?.taxStatus}
                                onChange={this.handleTaxStatusChange}
                            />
                        </div>
                    }
                    {this.resolveSaveButton()}
                </form>
            );
        }
    }

    private handleTaxCodeChange(taxCodeValue: string): void {
        this.setState({ taxId: taxCodeValue });
    }

    private handleTaxStatusChange(taxStatusValue: TaxStatus): void {
        this.setState({ taxStatus: taxStatusValue });
    }

    private getCardElementOptions(): StripeCardElementOptions {
        return {
            hidePostalCode: true,
            style: {
                base: {
                    fontSize: '16px'
                }
            }
        };
    }

    private getAddressElementOptions(): StripeAddressElementOptions {
        const name = this.props.billingName ? this.props.billingName : this.props.description;
        const billingAddress = this.props.billingAddress;
        return {
            mode: 'billing',
            display: {
                name: 'full'
            },
            defaultValues: {
                address: {
                    line1: billingAddress?.line1,
                    line2: billingAddress?.line2,
                    city: billingAddress?.city,
                    state: billingAddress?.state,
                    postal_code: billingAddress?.postalCode,
                    country: billingAddress?.country || STRIPE_US_COUNTRY_CODE
                },
                name: name
            }
        };
    }

    private getBillingAddressSection(): JSX.Element | null {
        if (!this.props.billingAddress) {
            return null;
        }
        return (
            <div className="bottom minitext">
                {this.getBillingAddressSectionContent(this.props.billingAddress)}
            </div>
        );
    }

    private resolveSaveButton(): JSX.Element | null {
        return this.props.isCommitInitiated === undefined ?
            (
                <button
                    onClick={this.handleSaveClick}
                    disabled={this.state.saving || !this.state.isInputValid}
                    className={'btn btn-short' + (this.state.saving ? ' loading' : '')}
                >
                    <span>Save changes</span>
                </button>
            )
            : null;
    }

    private handleChangeClick(e: React.MouseEvent<HTMLElement>): void {
        e.preventDefault();
        if (this.props.onReset) {
            this.props.onReset();
        }
    }

    private handleSaveClick(): void {
        this.setState(
            { saving: true },
            () => {
                this.commit().then(() => this.setState({ saving: false }));
            });
    }

    private handleCardDataChange(e: any): void {
        if (this.props.onCardDataChange) {
            this.props.onCardDataChange(e.complete && !e.empty);
        }
        this.setState({ isCardDataComplete: e.complete && !e.empty });
    }

    private handleAddressDataChange(e: any): void {
        this.setState({ selectedCountry: e.value.address.country });
        const isInputValid = !e.isEmpty && e.complete;
        if (this.props.onAddressDataChange) {
            this.props.onAddressDataChange(isInputValid);
        }
    }

    private async commit(): Promise<TokenResult | null> {
        if (this.props.cardData || !this.props.stripe || !this.props.elements) {
            Promise.resolve(null);
        } else {
            const cardElement = this.props.elements.getElement(CardElement);
            const billingAddressElement = this.props.elements.getElement(AddressElement);
            let value, addressObject = null;
            if (billingAddressElement) {
                value = await billingAddressElement.getValue();
                addressObject = this.setBillingAddressData(value.value) as CreateTokenCardData;
            }
            const promise = this.props.stripe.createToken(cardElement as StripeCardElement, addressObject as CreateTokenCardData);
            const onCommit = this.props.onCommit;
            if (onCommit) {
                promise.then(async (result) => {
                    const taxInfoUpdateObject = this.buildTaxInfoObject(this.state.taxId, this.state.taxStatus);
                    return onCommit(result, taxInfoUpdateObject);
                });
            }
        }
        return Promise.resolve(null);
    }

    private buildTaxInfoObject(taxId?: string, taxStatus?: TaxStatus ): TaxInfo {
        let taxInfo: TaxInfo = {};
        if (taxId) {
            taxInfo.taxId = taxId;
        }
        if (taxStatus) {
            taxInfo.taxStatus = taxStatus;
        }
        return taxInfo;
    }

    private getBillingAddressSectionContent(billingAddress: BillingAddress): JSX.Element {
        const { billingName, description } = this.props;
        let {
            line1 = '',
            line2 = '',
            city = '',
            country = '',
            state = '',
            postalCode = ''
        } = billingAddress;

        if (state === null) {
            state = '';
        }
        if (postalCode === null) {
            postalCode = '';
        }
        const name = billingName ? `Name: ${billingName}` : `Name: ${description}`;
        const address = `${line1}${line2 ? `, ${line2}` : ''} ${city} ${country} ${state} ${postalCode}`;

        return (
            <>
                {name && <>{name}<br/></>}
                {`Address: ${address}`}
            </>
        );
    }

    private setBillingAddressData(elementValue: StripeAddressElementValue): CreateTokenCardData {
        const address = elementValue.address;
        const billingAddress = {} as CreateTokenCardData;
        if (address.line1) {
            billingAddress.address_line1 = address.line1;
        }

        if (address.line2) {
            billingAddress.address_line2 = address.line2;
        }

        if (address.state) {
            billingAddress.address_state = address.state;
        }

        if (address.postal_code) {
            billingAddress.address_zip = address.postal_code;
        }

        if (address.country) {
            billingAddress.address_country = address.country;
        }

        if (address.city) {
            billingAddress.address_city = address.city;
        }

        if (elementValue.name) {
            billingAddress.name = elementValue.name;
        }
        return billingAddress;
    }
}



// WEBPACK FOOTER //
// ./src/webportal/components/billing/details/stripe/StripeBillingDetailsInputForm.tsx